import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Link as RouterLink } from "react-router-dom";
import { TierPackageTypes } from "@doitintl/cmp-models";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import capitalize from "lodash/capitalize";

import { useAvaCaseIQContext } from "../../../Components/Ava2/AvaCaseIQProvider";
import { useCategoryListFromPlatform } from "../../../Components/Ava2/hooks";
import { useCloudConnect } from "../../../Components/hooks/useCloudConnect";
import { defaultCurrencySymbol } from "../../../constants/common";
import { asyncConvertCurrencyTo } from "../../../Context/AsyncCurrencyConverterContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { useIsFeatureEntitled, useTier } from "../../../Context/TierProvider";
import { isCustomerInPresentationMode } from "../../../Context/useCustomerOrPresentationModeCustomer";
import { formatCurrency } from "../../../utils/common";
import { useFullScreen } from "../../../utils/dialog";
import mixpanel from "../../../utils/mixpanel";
import { getAnomalyInactiveDate, getCostOfAnomaly, getElementTimestamp } from "../AlertTools";
import {
  type AlertData,
  formatDateTime,
  getAnomalyDuration,
  getAnomalyStartTime,
  levelChipColor,
  statusChipColor,
} from "../utils";
import { checkIfRealTimeData, getSource, getSourceTooltip } from "../utils";
import { isRealTimeAnomaliesEnabled } from "../utils/realTimeUtils";
import AnomalyAcknowledgementForm from "./AnomalyAcknowledgementForm";
import InfoBlock from "./AnomalyInfoBlock";
import InfoRow from "./AnomalyInfoRow";
import EditAcknowledgementDialog from "./EditAcknowledgementDialog";
import RealTimePromotionCard from "./RealTimePromotionCard";
import { buildAnomalySupportUrl, isCREPlatform, isEnhancedOrEnterpriseTier } from "./utils";

const levelText = ["No alert", "Information", "Warning", "Critical"];

type AnomalyDetailsContentProps = {
  alertData: AlertData;
};

const AnomalyDetailsContent = ({ alertData }: AnomalyDetailsContentProps) => {
  const { isMobile } = useFullScreen("md", { noSsr: true }); // noSsr flag needed to get exact response on first render
  const [acknowledgeFormOpen, setAcknowledgeFormOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const manageButtonRef = useRef({ clientWidth: 150 });
  const { customer, assets } = useCustomerContext();
  const { categoryList } = useAvaCaseIQContext();
  const categories = useCategoryListFromPlatform(categoryList, alertData.metadata.platform);
  const isEntitledAcknowledgement = useIsFeatureEntitled("governance:costAnomalies:acknowledgement");
  const isEntitledRealTime = useIsFeatureEntitled("governance:costAnomalies:realTime");
  const [cloudConnectDocs] = useCloudConnect(customer);
  // Detect customer tiers
  const { getCustomerTier } = useTier();
  const navigatorTier = getCustomerTier(TierPackageTypes.NAVIGATOR);
  const navigatorTierName = navigatorTier?.name?.toLowerCase() ?? "";
  const isEnhancedOrEnterprise = isEnhancedOrEnterpriseTier(navigatorTierName);

  const isInPresentationMode = isCustomerInPresentationMode(customer);
  const open = Boolean(anchorEl);
  const id = open ? "manage-anomaly-popover" : undefined;
  const customerCurrency = customer.settings?.currency ?? defaultCurrencySymbol;
  const { customerFeedback } = alertData;
  const acknowledged = !!customerFeedback;
  const isSupportedPlatform = isCREPlatform(alertData.metadata.platform);

  const acknowledgementAllowed = useMemo(
    () => isEntitledAcknowledgement || isInPresentationMode,
    [isEntitledAcknowledgement, isInPresentationMode]
  );
  const matchingCategory = useMemo(
    () => categories.find((category) => category.name.includes(alertData.metadata.service_name || ""))?.id || "",
    [categories, alertData.metadata.service_name]
  );

  const closeManagePopover = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const [anomalyCostInCurrency, setAnomalyCostInCurrency] = useState<string>();

  useEffect(() => {
    const getAnomalyCostInCurrency = async () => {
      const costOfAnomaly = alertData ? getCostOfAnomaly(alertData) : 0;

      const converted = await asyncConvertCurrencyTo(
        costOfAnomaly,
        new Date(),
        defaultCurrencySymbol,
        customerCurrency
      );

      setAnomalyCostInCurrency(formatCurrency(converted, customerCurrency));
    };
    getAnomalyCostInCurrency();
  }, [alertData, customerCurrency]);

  const billingAccountName: string | null = useMemo(() => {
    if (
      !assets ||
      (!alertData.metadata.billing_account_id &&
        ["google-cloud", "microsoft-azure"].includes(alertData.metadata.platform)) ||
      (alertData.metadata.platform === "amazon-web-services" && !alertData.metadata.project_id)
    ) {
      return null;
    }

    const billingAccount = Object.values(assets)
      .flat()
      .find(
        (asset) =>
          asset.id.includes(alertData.metadata.billing_account_id) ||
          asset.id.includes(alertData.metadata.project_id ?? "")
      );

    if (!billingAccount?.data?.properties) {
      return null;
    }
    if (alertData.metadata.platform === "google-cloud") {
      return billingAccount.data.properties.displayName;
    }
    if (alertData.metadata.platform === "microsoft-azure") {
      return billingAccount.data.properties.subscription?.displayName;
    }
    if (alertData.metadata.platform === "amazon-web-services") {
      return billingAccount.data.properties.name;
    }
  }, [alertData.metadata.billing_account_id, alertData.metadata.platform, alertData.metadata.project_id, assets]);

  // Check if real-time is actually enabled
  const isRealTimeEnabled = useMemo(() => isRealTimeAnomaliesEnabled(cloudConnectDocs), [cloudConnectDocs]);

  // Show real-time promotion if: not a real-time anomaly, entitled to real-time, not enabled, and for supported services
  const shouldShowRealTimePromotion = useMemo(() => {
    const isRealTimeAnomaly = alertData.metadata.source === "real-time";
    const supportedServices = ["Amazon Elastic Compute Cloud", "Amazon Relational Database Service"];
    const isSupportedService = supportedServices.includes(alertData.metadata.service_name || "");

    return !isRealTimeAnomaly && isEntitledRealTime && !isRealTimeEnabled && isSupportedService;
  }, [alertData.metadata.source, alertData.metadata.service_name, isEntitledRealTime, isRealTimeEnabled]);

  const anomalyStartDate = getAnomalyStartTime(alertData.metadata, { zone: "utc" });
  const anomalyInactiveDate = getAnomalyInactiveDate(alertData);
  const source = getSource(alertData.metadata);
  const isRealTimeData = checkIfRealTimeData(alertData.metadata);

  return (
    <Grid
      container
      spacing={2}
      direction="row"
      sx={{
        mb: 5,
      }}
    >
      <InfoBlock title="Status">
        <InfoRow
          field="Status"
          value={
            <Chip
              variant="status"
              label={capitalize(alertData.status ?? "INACTIVE")}
              color={statusChipColor(alertData)}
            />
          }
        />
        <InfoRow field="Start date" value={formatDateTime(anomalyStartDate, isRealTimeData, true)} />
        <InfoRow
          field="End date"
          value={anomalyInactiveDate ? formatDateTime(anomalyInactiveDate, isRealTimeData, true) : "-"}
        />
        <InfoRow
          field="Duration"
          value={getAnomalyDuration({
            status: alertData.status,
            inactiveDate: anomalyInactiveDate,
            _ts: getElementTimestamp(alertData),
          })}
        />
      </InfoBlock>
      <InfoBlock title="Cost impact">
        <InfoRow field="Cost impact" value={anomalyCostInCurrency} />
        <InfoRow
          field="Severity"
          value={
            <Chip
              variant="status"
              label={
                !alertData.metadata.severity || alertData.metadata.severity === 1
                  ? "Info"
                  : levelText[alertData.metadata.severity]
              }
              color={levelChipColor[alertData.metadata.severity ?? 1]}
            />
          }
        />
        <InfoRow field="Source" value={source} tooltip={getSourceTooltip(alertData.metadata)} />
        {shouldShowRealTimePromotion && <RealTimePromotionCard />}
      </InfoBlock>
      <InfoBlock title="Context">
        {(billingAccountName || alertData.metadata.billing_account_id) && (
          <InfoRow
            field="Account name"
            value={
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                {billingAccountName &&
                  (alertData.metadata.platform === "amazon-web-services" ? (
                    billingAccountName
                  ) : (
                    <>
                      ({billingAccountName}) <br />
                    </>
                  ))}
                {alertData.metadata.platform !== "amazon-web-services" && alertData.metadata.billing_account_id}
              </Typography>
            }
          />
        )}
        <InfoRow field="Project/Account" value={alertData.metadata.project_id ?? "All"} />
        <InfoRow field="Service" value={alertData.metadata.service_name} />
        <InfoRow field="SKU" value={alertData.metadata.sku_name ?? "All"} />
      </InfoBlock>
      <InfoBlock title="Next steps">
        {isSupportedPlatform && isEnhancedOrEnterprise && (
          <Box mb={1}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 0.5,
              }}
            >
              {isEnhancedOrEnterprise ? "Get expert advice from DoiT" : "Get help and assistance"}
            </Typography>
            <Box color="text.secondary" fontSize={14}>
              Our engineers will help find a root cause for this anomaly and help prevent future anomalies
            </Box>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              component={RouterLink}
              to={buildAnomalySupportUrl(alertData, customer.id, matchingCategory || "")}
              onClick={() => {
                mixpanel.track("anomalies.detail.request-expert-advice", {
                  source: getSource(alertData.metadata),
                });
              }}
              sx={{ mb: 1, mt: 0.5 }}
              data-cy="anomaly-get-expert-advice-link"
            >
              {isEnhancedOrEnterprise ? "Contact a human expert" : "Access our knowledge base"}
            </Button>
          </Box>
        )}
        <InfoRow
          field="Acknowledged"
          value={acknowledged && acknowledgementAllowed ? "Yes" : "No"}
          onClick={
            acknowledged && acknowledgementAllowed
              ? () => {
                  setEditDialogOpen(true);
                }
              : undefined
          }
        />
        {!isMobile && acknowledgementAllowed && (
          <Box sx={{ flexGrow: 1, alignContent: "flex-end", width: "100%" }} ref={manageButtonRef}>
            <Button
              onClick={() => {
                setAcknowledgeFormOpen(true);
              }}
              sx={{ width: "100%", cursor: "pointer" }}
              variant="outlined"
              data-cy="open-acknowledgement-button"
              id={id}
            >
              {acknowledged ? "Edit acknowledgement" : "Acknowledge anomaly"}
            </Button>
          </Box>
        )}
        {editDialogOpen && customerFeedback && (
          <EditAcknowledgementDialog
            customerFeedback={customerFeedback}
            onConfirm={() => {
              setAcknowledgeFormOpen(true);
              setEditDialogOpen(false);
              closeManagePopover();
            }}
            onCancel={() => {
              setEditDialogOpen(false);
              closeManagePopover();
            }}
          />
        )}
        {acknowledgeFormOpen && (
          <AnomalyAcknowledgementForm
            onClose={() => {
              setAcknowledgeFormOpen(false);
              closeManagePopover();
            }}
            customerFeedback={customerFeedback}
            source={source}
          />
        )}
      </InfoBlock>
    </Grid>
  );
};

export default AnomalyDetailsContent;
